import { useCallback, useEffect, useRef, useState } from "react";

import type { AuditionMode } from "./MediaUnion";
import type { FxIntentType } from "./NarrativeSonics.types";

export type WhisperSeverity = "info" | "mode" | "warning";

export interface WhisperEvent {
  id: string;
  text: string;
  severity: WhisperSeverity;
  createdAt: number;
  ttlMs?: number;
  persistHint?: boolean;
}

export const DEFAULT_WHISPER_TTL_MS: Record<WhisperSeverity, number> = {
  info: 1800,
  mode: 2600,
  warning: 3800,
};

export interface TimelineWhisperHudState {
  activeEvent: WhisperEvent | null;
  queue: WhisperEvent[];
}

export type TimelineWhisperHudIntent =
  | { type: "whisper/show"; event: WhisperEvent }
  | { type: "whisper/dismiss"; id: string }
  | { type: "whisper/clear" };

export type TimelineWhisperSourceIntent =
  | { type: "selection/changed"; clipTitle: string }
  | { type: "audition/modeChanged"; mode: AuditionMode }
  | { type: "arc/upserted"; operation: "added" | "updated" }
  | { type: "fx/upserted"; operation: "added" | "updated"; nodeType?: FxIntentType }
  | { type: "continuity/warningSurfaced"; atSec: number };

const DUPLICATE_DEBOUNCE_MS = 900;
const SEVERITY_PRIORITY: Record<WhisperSeverity, number> = {
  info: 1,
  mode: 2,
  warning: 3,
};

function makeWhisperId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }

  return `whisper-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function clampPriorityQueue(queue: WhisperEvent[]): WhisperEvent[] {
  return [...queue].sort((left, right) => {
    const priorityDelta = SEVERITY_PRIORITY[right.severity] - SEVERITY_PRIORITY[left.severity];
    if (priorityDelta !== 0) return priorityDelta;
    return left.createdAt - right.createdAt;
  });
}

function isDuplicateWhisper(left: WhisperEvent, right: WhisperEvent): boolean {
  return left.text === right.text && left.severity === right.severity;
}

function compactLabel(text: string, maxLength = 40): string {
  const clean = String(text ?? "").trim();
  if (!clean) return "Untitled";
  if (clean.length <= maxLength) return clean;
  return `${clean.slice(0, maxLength - 3).trimEnd()}...`;
}

function titleCaseMode(mode: AuditionMode): string {
  switch (mode) {
    case "sequence":
      return "Sequence";
    case "source":
      return "Source";
    default:
      return "Clip";
  }
}

export function formatWhisperSeconds(sec: number): string {
  const safe = Number.isFinite(sec) ? Math.max(0, sec) : 0;
  return `${safe.toFixed(2).padStart(5, "0")}s`;
}

export function createWhisperEvent(intent: TimelineWhisperSourceIntent): WhisperEvent {
  const createdAt = Date.now();

  switch (intent.type) {
    case "selection/changed":
      return {
        id: makeWhisperId(),
        text: `Selection: ${compactLabel(intent.clipTitle)}`,
        severity: "info",
        createdAt,
      };
    case "audition/modeChanged":
      return {
        id: makeWhisperId(),
        text: `Audition mode: ${titleCaseMode(intent.mode)}`,
        severity: "mode",
        createdAt,
      };
    case "arc/upserted":
      return {
        id: makeWhisperId(),
        text: intent.operation === "updated" ? "ARC segment updated" : "ARC segment added",
        severity: "info",
        createdAt,
      };
    case "fx/upserted": {
      const nodeLabel = intent.nodeType ? `: ${intent.nodeType}` : "";
      return {
        id: makeWhisperId(),
        text: intent.operation === "updated" ? `FX node updated${nodeLabel}` : `FX node added${nodeLabel}`,
        severity: "info",
        createdAt,
      };
    }
    case "continuity/warningSurfaced":
      return {
        id: makeWhisperId(),
        text: `Continuity warning near ${formatWhisperSeconds(intent.atSec)}`,
        severity: "warning",
        createdAt,
        persistHint: true,
      };
    default:
      return {
        id: makeWhisperId(),
        text: "Editorial update",
        severity: "info",
        createdAt,
      };
  }
}

function resolveTtlMs(event: WhisperEvent): number {
  return event.ttlMs ?? DEFAULT_WHISPER_TTL_MS[event.severity];
}

function enqueueWhisper(queue: WhisperEvent[], event: WhisperEvent): WhisperEvent[] {
  const nextQueue = queue.filter((entry) => !isDuplicateWhisper(entry, event));
  nextQueue.push(event);

  const prunedQueue = nextQueue.filter((entry, index) => {
    const firstMatchIndex = nextQueue.findIndex((candidate) => isDuplicateWhisper(candidate, entry));
    return firstMatchIndex === index;
  });

  return clampPriorityQueue(prunedQueue);
}

function shouldPreempt(activeEvent: WhisperEvent, incomingEvent: WhisperEvent): boolean {
  const activePriority = SEVERITY_PRIORITY[activeEvent.severity];
  const incomingPriority = SEVERITY_PRIORITY[incomingEvent.severity];

  if (incomingPriority > activePriority) return true;
  if (activeEvent.severity === "info" && incomingEvent.severity === "info") return true;
  return false;
}

function reduceWhisperState(
  state: TimelineWhisperHudState,
  intent: TimelineWhisperHudIntent,
  lastSeen: { text: string; severity: WhisperSeverity; at: number } | null,
): TimelineWhisperHudState {
  switch (intent.type) {
    case "whisper/show": {
      const event = {
        ...intent.event,
        ttlMs: resolveTtlMs(intent.event),
      };

      if (
        lastSeen &&
        lastSeen.text === event.text &&
        lastSeen.severity === event.severity &&
        event.createdAt - lastSeen.at < DUPLICATE_DEBOUNCE_MS
      ) {
        if (state.activeEvent && isDuplicateWhisper(state.activeEvent, event)) {
          return {
            activeEvent: {
              ...state.activeEvent,
              createdAt: event.createdAt,
              ttlMs: event.ttlMs,
              persistHint: event.persistHint ?? state.activeEvent.persistHint,
            },
            queue: state.queue,
          };
        }

        return state;
      }

      if (!state.activeEvent) {
        return {
          activeEvent: event,
          queue: state.queue.filter((entry) => !isDuplicateWhisper(entry, event)),
        };
      }

      if (isDuplicateWhisper(state.activeEvent, event)) {
        return {
          activeEvent: {
            ...state.activeEvent,
            createdAt: event.createdAt,
            ttlMs: event.ttlMs,
            persistHint: event.persistHint ?? state.activeEvent.persistHint,
          },
          queue: state.queue.filter((entry) => !isDuplicateWhisper(entry, event)),
        };
      }

      if (shouldPreempt(state.activeEvent, event)) {
        const nextQueue = event.severity === "warning" ? state.queue.filter((entry) => entry.severity !== "info") : state.queue;
        return {
          activeEvent: event,
          queue: nextQueue.filter((entry) => !isDuplicateWhisper(entry, event)),
        };
      }

      if (event.severity === "info") {
        return state;
      }

      return {
        activeEvent: state.activeEvent,
        queue: enqueueWhisper(state.queue, event),
      };
    }
    case "whisper/dismiss": {
      if (state.activeEvent?.id === intent.id) {
        const [nextActiveEvent, ...restQueue] = state.queue;
        return {
          activeEvent: nextActiveEvent ?? null,
          queue: restQueue,
        };
      }

      return {
        activeEvent: state.activeEvent,
        queue: state.queue.filter((entry) => entry.id !== intent.id),
      };
    }
    case "whisper/clear":
      return {
        activeEvent: null,
        queue: [],
      };
    default:
      return state;
  }
}

export function useTimelineWhisperHUD() {
  const [state, setState] = useState<TimelineWhisperHudState>({
    activeEvent: null,
    queue: [],
  });
  const dismissTimeoutRef = useRef<number | null>(null);
  const lastSeenRef = useRef<{ text: string; severity: WhisperSeverity; at: number } | null>(null);

  const dispatch = useCallback((intent: TimelineWhisperHudIntent) => {
    const previousLastSeen = lastSeenRef.current;

    setState((prev) => {
      const nextState = reduceWhisperState(prev, intent, previousLastSeen);

      if (intent.type === "whisper/show" && nextState !== prev) {
        lastSeenRef.current = {
          text: intent.event.text,
          severity: intent.event.severity,
          at: intent.event.createdAt,
        };
      }

      return nextState;
    });
  }, []);

  useEffect(() => {
    if (dismissTimeoutRef.current != null) {
      window.clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }

    if (!state.activeEvent) return;

    dismissTimeoutRef.current = window.setTimeout(() => {
      dismissTimeoutRef.current = null;
      dispatch({ type: "whisper/dismiss", id: state.activeEvent?.id ?? "" });
    }, resolveTtlMs(state.activeEvent));

    return () => {
      if (dismissTimeoutRef.current != null) {
        window.clearTimeout(dismissTimeoutRef.current);
        dismissTimeoutRef.current = null;
      }
    };
  }, [dispatch, state.activeEvent]);

  const showEvent = useCallback(
    (event: WhisperEvent) => {
      dispatch({ type: "whisper/show", event });
    },
    [dispatch],
  );

  const showIntent = useCallback(
    (intent: TimelineWhisperSourceIntent) => {
      showEvent(createWhisperEvent(intent));
    },
    [showEvent],
  );

  return {
    state,
    dispatch,
    showEvent,
    showIntent,
  };
}